import type { Song } from '../types/domain';
import { parseLength } from './formatting';

export const SONG_LENGTH_STORAGE_KEY = 'Lyrixa.songLengths';
export const DEFAULT_GENERATED_RUNTIME = '2:00';

export function effectiveSongLength(length: string | null | undefined): string {
  if (!length) return DEFAULT_GENERATED_RUNTIME;
  const seconds = parseLength(length);
  return Number.isFinite(seconds) && seconds > 0 ? length : DEFAULT_GENERATED_RUNTIME;
}

export function readPersistedSongLengths(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SONG_LENGTH_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out: Record<string, string> = {};
    for (const [songId, length] of Object.entries(parsed)) {
      if (typeof length === 'string' && parseLength(length) > 0) {
        out[songId] = length;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function persistSongLength(songId: string, length: string): void {
  if (typeof window === 'undefined' || !songId || parseLength(length) <= 0) return;
  const next = readPersistedSongLengths();
  next[songId] = length;
  try {
    window.localStorage.setItem(SONG_LENGTH_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage may be unavailable; the in-memory song is still updated and the browser will
    // keep the most recent value until the next successful write attempt.
  }
}

export function hydratePersistedSongLengths(songs: readonly Song[]): Song[] {
  const persisted = readPersistedSongLengths();
  if (Object.keys(persisted).length === 0) {
    return songs.map((song) => ({ ...song, length: effectiveSongLength(song.length) }));
  }

  return songs.map((song) => {
    const persistedLength = persisted[song.id];
    if (persistedLength && parseLength(persistedLength) > 0) {
      return { ...song, length: persistedLength };
    }
    return { ...song, length: effectiveSongLength(song.length) };
  });
}
